/**
 * Utilitário para detectar bloqueadores de anúncios
 * 
 * Este utilitário tenta fazer uma requisição para um endpoint conhecido do Sentry
 * para verificar se há um bloqueador de anúncios ativo. Se detectado, o Sentry
 * deve usar o modo tunnel através do backend da aplicação.
 */

const SENTRY_DSN = 'https://795d22942ea863d14e35974c9051793a@o4509538530951168.ingest.us.sentry.io/4510293420408832'

/**
 * Extrai a URL do endpoint do Sentry a partir do DSN
 * @param {string} dsn - DSN do Sentry
 * @returns {string} URL do endpoint do Sentry
 */
function getSentryEndpoint(dsn) {
  try {
    // Extrai o host e project ID do DSN
    // Formato: https://PUBLIC_KEY@HOST/PROJECT_ID
    const match = dsn.match(/https:\/\/([^@]+)@([^/]+)\/(.+)/)
    if (!match) {
      return null
    }
    
    const [, , host, projectId] = match
    // Endpoint para teste - usa o endpoint de envelope do Sentry
    return `https://${host}/api/${projectId}/envelope/`
  } catch (error) {
    console.error('[AdBlocker Detection] Erro ao extrair endpoint do DSN:', error)
    return null
  }
}

/**
 * Detecta se há um bloqueador de anúncios ativo
 * 
 * A estratégia é tentar fazer uma requisição para o endpoint do Sentry.
 * Se a requisição falhar ou for bloqueada (ERR_BLOCKED_BY_CLIENT, CORS, network error, etc),
 * assumimos que há um bloqueador de anúncios.
 * 
 * @returns {Promise<boolean>} true se bloqueador detectado, false caso contrário
 */
export async function detectAdBlocker() {
  // Se não estiver no browser, retorna false
  if (typeof window === 'undefined' || typeof fetch === 'undefined') {
    return false
  }

  const endpoint = getSentryEndpoint(SENTRY_DSN)
  if (!endpoint) {
    console.warn('[AdBlocker Detection] Não foi possível extrair endpoint do DSN')
    return false
  }

  try {
    // Tenta fazer uma requisição POST para o endpoint do Sentry (similar ao que o Sentry faz)
    // Usa AbortController para timeout de 2 segundos
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 2000)

    // Tenta fazer uma requisição simples para verificar se é bloqueada
    const response = await fetch(endpoint, {
      method: 'POST',
      mode: 'cors', // cors permite detectar bloqueios melhor que no-cors
      signal: controller.signal,
      cache: 'no-cache',
      headers: {
        'Content-Type': 'application/x-sentry-envelope'
      },
      body: '' // Body vazio para teste
    })

    clearTimeout(timeoutId)
    
    // Se chegou aqui sem erro, provavelmente não foi bloqueado
    // Mas mesmo assim pode ter sido bloqueado silenciosamente
    // Verificamos se a resposta foi bem-sucedida
    return false
  } catch (error) {
    // Se der erro de rede, timeout, abort, ou bloqueio, pode ser bloqueador
    // Erros comuns: TypeError, NetworkError, AbortError, ERR_BLOCKED_BY_CLIENT
    const errorMessage = error.message || error.toString() || ''
    const errorName = error.name || ''
    
    // Verifica vários tipos de erros que indicam bloqueio
    const isBlocked = 
      errorName === 'TypeError' ||
      errorName === 'NetworkError' ||
      errorName === 'AbortError' ||
      errorMessage.includes('Failed to fetch') ||
      errorMessage.includes('Network request failed') ||
      errorMessage.includes('ERR_BLOCKED_BY_CLIENT') ||
      errorMessage.includes('blocked') ||
      errorMessage.includes('CORS') ||
      // Verifica se o erro é relacionado a bloqueio silencioso
      (errorName === 'TypeError' && errorMessage.includes('Failed to fetch'))
    
    if (isBlocked) {
      console.warn('[AdBlocker Detection] Bloqueador de anúncios detectado:', errorName, errorMessage)
      return true
    }
    
    // Outros erros podem não ser relacionados a bloqueador
    console.warn('[AdBlocker Detection] Erro ao verificar bloqueador:', error)
    return false
  }
}

/**
 * Detecta bloqueador usando uma abordagem alternativa
 * Cria um elemento conhecido por ser bloqueado por ad blockers
 * 
 * @returns {Promise<boolean>} true se bloqueador detectado, false caso contrário
 */
export async function detectAdBlockerAlternative() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return false
  }

  return new Promise((resolve) => {
    // Cria um elemento que é comum em listas de bloqueio
    const testElement = document.createElement('div')
    testElement.innerHTML = '&nbsp;'
    testElement.className = 'adsbox'
    testElement.style.position = 'absolute'
    testElement.style.left = '-9999px'
    testElement.style.width = '1px'
    testElement.style.height = '1px'
    
    document.body.appendChild(testElement)
    
    // Verifica se o elemento foi removido ou ocultado pelo bloqueador
    setTimeout(() => {
      const isBlocked = 
        testElement.offsetHeight === 0 ||
        testElement.offsetWidth === 0 ||
        testElement.style.display === 'none' ||
        testElement.style.visibility === 'hidden' ||
        !document.body.contains(testElement)
      
      document.body.removeChild(testElement)
      
      if (isBlocked) {
        console.warn('[AdBlocker Detection] Bloqueador detectado via método alternativo')
        resolve(true)
      } else {
        resolve(false)
      }
    }, 100)
  })
}

/**
 * Detecta bloqueador usando múltiplas estratégias
 * Retorna true se qualquer método detectar bloqueador
 * 
 * @returns {Promise<boolean>} true se bloqueador detectado, false caso contrário
 */
export async function detectAdBlockerCombined() {
  try {
    // Executa ambas as detecções em paralelo
    const [method1, method2] = await Promise.all([
      detectAdBlocker(),
      detectAdBlockerAlternative()
    ])
    
    const isBlocked = method1 || method2
    
    if (isBlocked) {
      console.warn('[AdBlocker Detection] Bloqueador de anúncios detectado. Usando tunnel para Sentry.')
    } else {
      console.log('[AdBlocker Detection] Nenhum bloqueador detectado.')
    }
    
    return isBlocked
  } catch (error) {
    console.error('[AdBlocker Detection] Erro durante detecção:', error)
    // Em caso de erro, assumimos que não há bloqueador para não bloquear
    // o funcionamento normal do Sentry
    return false
  }
}

/**
 * Cache do resultado da detecção para evitar múltiplas verificações
 */
let detectionCache = null
let detectionPromise = null

/**
 * Detecta bloqueador com cache
 * Executa a detecção apenas uma vez e retorna resultado em cache nas próximas chamadas
 * 
 * @returns {Promise<boolean>} true se bloqueador detectado, false caso contrário
 */
export async function detectAdBlockerCached() {
  if (detectionCache !== null) {
    return detectionCache
  }
  
  if (detectionPromise) {
    return detectionPromise
  }
  
  detectionPromise = detectAdBlockerCombined()
  detectionCache = await detectionPromise
  
  return detectionCache
}

